import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import type { Locale } from '@sonasi/shared-types';

import { gqlClient } from '@/api';
import type { LocaleProgress } from './apps';
import { rememberWebsiteUrl, useShareImage } from './share-image';

/**
 * The page copy the website shows outside any app.
 *
 * The home page and the Support page read this on every request, and until now
 * it could only be changed by running `yarn api:site-import` on a machine
 * holding the database credentials.
 */

/** A field written in several languages. Untyped values: lists as well as strings. */
export type Bag = Record<string, unknown>;

export interface PrincipleRow {
  title: string;
  description: string;
}

export interface FaqRow {
  question: string;
  answer: string;
}

export interface AdminSiteContent {
  heroKicker: Bag;
  heroTitle: Bag;
  heroSubtitle: Bag;
  principlesLabel: Bag;
  principlesTitle: Bag;
  principles: Bag;
  supportSubtitle: Bag;
  supportContactNote: Bag;
  supportBusinessNote: Bag;
  faq: Bag;
  supportEmail: string;
  businessEmail: string;
  /** Where else the studio is; published as the organisation's `sameAs`. */
  links: string[];
  /** The picture a link to the site unfurls with, when one was chosen; null means the website draws it. */
  shareImageUrl: string | null;
  locales: LocaleProgress[];
  updatedAt: string;
}

const FIELDS = `
  heroKicker
  heroTitle
  heroSubtitle
  principlesLabel
  principlesTitle
  principles
  supportSubtitle
  supportContactNote
  supportBusinessNote
  faq
  supportEmail
  businessEmail
  links
  shareImageUrl
  locales { locale translated total }
  updatedAt
`;

const ADMIN_SITE_CONTENT = gql`
  query AdminSiteContent {
    adminSiteContent { ${FIELDS} }
    websiteUrl
  }
`;

const UPDATE_SITE_CONTENT = gql`
  mutation UpdateSiteContent($locale: ContentLocale!, $content: SiteContentInput!) {
    updateSiteContent(locale: $locale, content: $content) { ${FIELDS} }
  }
`;

const UPDATE_SITE_ADDRESSES = gql`
  mutation UpdateSiteAddresses($input: SiteAddressesInput!) {
    updateSiteAddresses(input: $input) { ${FIELDS} }
  }
`;

const UPDATE_SITE_LINKS = gql`
  mutation UpdateSiteLinks($input: SiteLinksInput!) {
    updateSiteLinks(input: $input) { ${FIELDS} }
  }
`;

export interface SiteContentInput {
  heroKicker?: string;
  heroTitle?: string;
  heroSubtitle?: string;
  principlesLabel?: string;
  principlesTitle?: string;
  principles?: PrincipleRow[];
  supportSubtitle?: string;
  supportContactNote?: string;
  supportBusinessNote?: string;
  faq?: FaqRow[];
}

export const useSiteContent = () => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['adminSiteContent'],
    queryFn: async () => {
      const data = await gqlClient.request<{
        adminSiteContent: AdminSiteContent;
        websiteUrl?: string;
      }>(ADMIN_SITE_CONTENT);
      rememberWebsiteUrl(queryClient, data.websiteUrl);
      return data.adminSiteContent;
    },
  });
};

/**
 * Saves the copy and the addresses together, in that order.
 *
 * One Save for the screen, two mutations — the same shape `useSaveApp` uses,
 * and for the same reason: the addresses are not per language, so they cannot
 * ride along in the localized input without implying that a Japanese visitor
 * gets a different support address.
 */
export const useSaveSiteContent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      locale: Locale;
      content?: SiteContentInput;
      addresses?: { supportEmail: string; businessEmail: string };
      /** The studio's other addresses, the same in every language. */
      links?: string[];
    }) => {
      let saved: AdminSiteContent | null = null;
      if (input.content) {
        saved = (
          await gqlClient.request<{ updateSiteContent: AdminSiteContent }>(UPDATE_SITE_CONTENT, {
            locale: input.locale.toUpperCase(),
            content: input.content,
          })
        ).updateSiteContent;
      }
      if (input.addresses) {
        saved = (
          await gqlClient.request<{ updateSiteAddresses: AdminSiteContent }>(
            UPDATE_SITE_ADDRESSES,
            { input: input.addresses },
          )
        ).updateSiteAddresses;
      }
      if (input.links) {
        saved = (
          await gqlClient.request<{ updateSiteLinks: AdminSiteContent }>(UPDATE_SITE_LINKS, {
            input: { links: input.links },
          })
        ).updateSiteLinks;
      }
      return saved;
    },
    onSuccess: (saved) => {
      if (saved) queryClient.setQueryData(['adminSiteContent'], saved);
    },
  });
};

const SET_SITE_SHARE_IMAGE = gql`
  mutation SetSiteShareImage($fileId: ID) {
    setSiteShareImage(fileId: $fileId) {
      shareImageUrl
      updatedAt
    }
  }
`;

/** The picture a link to the site unfurls with. */
export const useSiteShareImage = () =>
  useShareImage<AdminSiteContent>({
    mutation: SET_SITE_SHARE_IMAGE,
    variables: {},
    queryKey: ['adminSiteContent'],
  });

/** One language out of a bag, with no fallback — an editor shows what is there. */
export const inLocale = <T>(bag: Bag | undefined, locale: Locale, fallback: T): T =>
  (bag?.[locale] as T | undefined) ?? fallback;
